import {
  ResourceTemplate,
  type ReadResourceCallback,
} from "@modelcontextprotocol/sdk/server/mcp.js";
import type { RequestHandlerExtra } from "@modelcontextprotocol/sdk/shared/protocol.js";
import type { ReadResourceResult, Resource } from "@modelcontextprotocol/sdk/types.js";

async function fetchRawGithubContent(rawPath: string) {
  const path = rawPath.replace("/blob", "");

  return fetch(`https://raw.githubusercontent.com${path}`).then((res) => res.text());
}

async function defaultGitHubHandler(url: URL): Promise<ReadResourceResult> {
  const uri = url.host;
  const rawPath = url.pathname;
  const content = await fetchRawGithubContent(rawPath);
  return {
    contents: [
      {
        uri: uri,
        mimeType: "text/plain",
        text: content,
      },
    ],
  };
}

async function convexDocsHandler(
  url: URL,
  _extra: RequestHandlerExtra<any, any>,
): Promise<ReadResourceResult> {
  // Make sure the docs pages being requested actually have .md versions!
  const path = `${url.pathname.replace(/\/$/, "")}.md`;
  const mdUrl = `${url.origin}${path}`;

  const response = await fetch(mdUrl);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `Convex documentation not found at ${mdUrl}. Please check the URL is correct.`,
      );
    }
    throw new Error(`Failed to fetch Convex docs: ${response.status} ${response.statusText}`);
  }

  const content = await response.text();

  return {
    contents: [
      {
        uri: url.toString(),
        mimeType: "text/markdown",
        text: content,
      },
    ],
  };
}

const buildTextHandler =
  (content: string) =>
  async (uri: URL): Promise<ReadResourceResult> => {
    return {
      contents: [
        {
          uri: uri.toString(),
          mimeType: "text/plain",
          text: content,
        },
      ],
    };
  };

const QUICKSTARTS = [
  "android",
  "bun",
  "nextjs",
  "nodejs",
  "python",
  "react-native",
  "react",
  "remix",
  "rust",
  "script-tag",
  "svelte",
  "swift",
  "tanstack-start",
  "vue",
] as const;

const CLIENTS = [
  "android",
  "javascript",
  "open-api",
  "python",
  "react-native",
  "react",
  "rust",
  "svelte",
  "swift",
  "tanstack-query",
  "vue",
];

export type ResourceConfig = {
  name: string;
  title: string;
  description: string;
  mimeType: string;
  handler: ReadResourceCallback;
} & ({ uri: string; template?: never } | { uri?: never; template: ResourceTemplate });

export const resources = [
  {
    name: "convex-docs-quickstart",
    title: "Convex docs quickstarts",
    template: new ResourceTemplate("https://docs.convex.dev/quickstart/{platform}/", {
      list: async (_extra) => ({
        resources: QUICKSTARTS.map((quickstart) => ({
          uri: `https://docs.convex.dev/quickstart/${quickstart}/`,
          name: `${quickstart}-quickstart`,
          description: `Convex quickstart documentation for ${quickstart}`,
          mimeType: "text/markdown",
        })),
      }),
    }),
    mimeType: "text/markdown",
    description: "Convex quickstart documentation for {platform}",
    handler: convexDocsHandler,
  },
  {
    // TODO this isn't perfect, we need full docs in here instead
    name: "convex-docs-clients",
    title: "Convex client docs",
    template: new ResourceTemplate("https://docs.convex.dev/client/{client}/", {
      list: async (_extra) => ({
        resources: CLIENTS.map((client) => ({
          uri: `https://docs.convex.dev/client/${client}/`,
          name: `${client}-client-docs`,
          description: `Convex SDK documentation for ${client}`,
          mimeType: "text/markdown",
        })),
      }),
    }),
    mimeType: "text/markdown",
    description: "Convex client documentation for {client}",
    handler: convexDocsHandler,
  },
  {
    name: "usage-with-builder.io",
    title: "Usage with Builder.io",
    uri: "https://mcp.convex.app/resources/builderio.mdc",
    mimeType: "text/plain",
    description: "How to use Convex with Builder.io / Fusion.",
    handler: buildTextHandler(
      `# Advice for using Convex MCP server with Builder.io / Fusion

Builder.io's environment variable tool doesn't work set the environment variables
appropriately for node.js tools to run, so after obtaining a CONVEX_DEPLOY_KEY it's
necessary to store it in a .env.local file instead of using their too.

After storing the project token in CONVEX_DEPLOY_KEY in a .env.local, convex
commands should work fine!
  `,
    ),
  },
  {
    name: "usage-with-react",
    title: "Usage with React",
    uri: "https://mcp.convex.app/resources/react.mdc",
    mimeType: "text/plain",
    description: "How to use Convex with React",
    handler: buildTextHandler(
      `# Advice for using Convex MCP server with React

To use Convex with React in the browser you need a ConvexReactClient, a ConvexProvider, and to
use hooks like useQuery in React components.

The provider and client should be created high up in the React tree, so 
creating a client and a provider might look like this:

\`\`\`
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { ConvexProvider, ConvexReactClient } from "convex/react";

const address = import.meta.env.VITE_CONVEX_URL;

const convex = new ConvexReactClient(address);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <ConvexProvider client={convex}>
    <App />
  </ConvexProvider>
);
\`\`\`

Then using Convex in a React component might look like this:

\`\`\`
import { FormEvent, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../convex/_generated/api";

export default function App() {
  const messages = useQuery(api.messages.list) || [];

  const [newMessageText, setNewMessageText] = useState("");
  const sendMessage = useMutation(api.messages.sendMessage);

  const [name] = useState(() => "User " + Math.floor(Math.random() * 10000));
  async function handleSendMessage(event: FormEvent) {
    event.preventDefault();
    if (newMessageText) {
      await sendMessage({ body: newMessageText, author: name });
    }
    setNewMessageText("");
  }

  const generateUploadUrl = useMutation(api.messages.generateUploadUrl);
  const sendImage = useMutation(api.messages.sendImage);

  const imageInput = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  async function handleSendImage(event: FormEvent) {
    event.preventDefault();

    // Step 1: Get a short-lived upload URL
    const postUrl = await generateUploadUrl();
    // Step 2: POST the file to the URL
    const result = await fetch(postUrl, {
      method: "POST",
      headers: { "Content-Type": selectedImage!.type },
      body: selectedImage,
    });
    const json = await result.json();
    if (!result.ok) {
      throw new Error(\`Upload failed: \${JSON.stringify(json)}\`);
    }
    const { storageId } = json;
    // Step 3: Save the newly allocated storage id to the database
    await sendImage({ storageId, author: name });

    setSelectedImage(null);
    imageInput.current!.value = "";
  }

  return (
    <main>
      <h1>Convex Chat</h1>
      <p className="badge">
        <span>{name}</span>
      </p>
      <ul>
        {messages.map((message) => (
          <li key={message._id}>
            <span>{message.author}:</span>
            {message.format === "image" ? (
              <Image message={message} />
            ) : (
              <span>{message.body}</span>
            )}
            <span>{new Date(message._creationTime).toLocaleTimeString()}</span>
          </li>
        ))}
      </ul>
      <form onSubmit={handleSendMessage}>
        <input
          value={newMessageText}
          onChange={(event) => setNewMessageText(event.target.value)}
          placeholder="Write a message…"
        />
        <input type="submit" value="Send" disabled={!newMessageText} />
      </form>
      <form onSubmit={handleSendImage}>
        <input
          type="file"
          accept="image/*"
          ref={imageInput}
          onChange={(event) => setSelectedImage(event.target.files![0])}
          className="ms-2 btn btn-primary"
          disabled={selectedImage !== null}
        />
        <input
          type="submit"
          value="Send Image"
          disabled={selectedImage === null}
        />
      </form>
    </main>
  );
}

function Image({ message }: { message: { url: string } }) {
  return <img src={message.url} height="300px" width="auto" />;
}
\`\`\`

  `,
    ),
  },
] satisfies ResourceConfig[];
